import type { JSX } from 'react';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Chip } from '@/components/blocks/feedback';
import { cn } from '@/lib/utils';
import { statusToneTextClass } from '@/lib/statusTone';
import type { PersonRecord } from '@/data/schemas';
import { buildPersonnelBadges } from '@/data/personnel/personnelScope';
import { formatIsoDateItShort } from '@/utils/date';
import { getDateStatus, getDateStatusTone } from '@/utils/dateStatus';

interface PersonnelCardProps {
  person: PersonRecord;
  onEdit: (personnelId: string) => void;
}

export function PersonnelCard({ person, onEdit }: PersonnelCardProps): JSX.Element {
  const fullName =
    [person.firstName, person.lastName].filter(Boolean).join(' ').trim() || person.lastName || '—';
  const badges = buildPersonnelBadges(person);
  const licenseStatus =
    person.licensed && person.licenseExpiry
      ? getDateStatus(person.licenseExpiry)
      : ('valid' as const);
  const expiryTone = getDateStatusTone(licenseStatus);
  const formattedExpiry = person.licenseExpiry
    ? formatIsoDateItShort(person.licenseExpiry, '—')
    : null;

  const infoRows: Array<{
    label: string;
    value: string;
    tone?: ReturnType<typeof getDateStatusTone>;
  }> = [];

  if (person.email) {
    infoRows.push({ label: 'Email', value: person.email });
  }
  if (person.phone) {
    infoRows.push({ label: 'Telefono', value: person.phone });
  }
  if (formattedExpiry) {
    infoRows.push({
      label: 'Scadenza patente',
      value: formattedExpiry,
      tone: expiryTone,
    });
  }

  return (
    <div className="flex h-full flex-col gap-3 rounded-lg border border-[var(--border-default)] bg-surface p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-text-primary">{fullName}</p>
          <p className="truncate text-sm text-text-secondary">{person.role || '—'}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(person.id)}
          className="text-text-secondary hover:text-text-primary"
          aria-label={`Modifica ${fullName}`}
        >
          <Icon name="edit" className="text-base" aria-hidden="true" />
        </Button>
      </div>

      {infoRows.length > 0 ? (
        <div className="space-y-2">
          {infoRows.map((row) => (
            <div key={row.label} className="space-y-0.5">
              <p className="text-xs text-text-secondary">{row.label}</p>
              <p
                className={cn(
                  'truncate text-sm text-text-primary',
                  row.tone && statusToneTextClass(row.tone)
                )}
              >
                {row.value}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-text-tertiary">Nessun contatto disponibile</p>
      )}

      {badges.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {badges.map((badge) => (
            <Chip tone={badge.tone} icon={badge.icon} key={badge.key}>
              {badge.label}
            </Chip>
          ))}
        </div>
      ) : null}
    </div>
  );
}
